import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import './LandingPage.css';
import booksData from './booksData';

const LandingPage = () => {
  const snMain = booksData.find(series => series.seriesName === 'Sam Norris Murder Mysteries');
  const snShorts = booksData.find(series => series.seriesName === 'Sam Norris Short Stories');

  const allSamBooks = [
    ...(snMain?.books || []),
    ...(snShorts?.books || [])
  ];

  const landingBook = allSamBooks.find(book => book.landingPage);
  const bookOne = snMain?.books.find(book => book.seriesName === 'SNMM' && book.bookNumber === 1);

  if (!landingBook) {
    return <div>No landing page book configured.</div>;
  }

  const pageUrl = 'https://jerryevanoff.com/landingpage';
  const pageTitle = 'Start the Sam Norris Murder Mystery Series | Jerry Evanoff';
  const pageDescription =
    'Start reading the Sam Norris Murder Mystery Series by Jerry Evanoff, featuring modern whodunits, amateur sleuth murder mysteries, tech-savvy programmer Sam Norris and old-school puzzle plots.';

  const makeAbsoluteUrl = (url) => {
    if (!url) {
      return 'https://jerryevanoff.com';
    }

    if (url.startsWith('http')) {
      return url;
    }

    return `https://jerryevanoff.com${url.startsWith('/') ? '' : '/'}${url}`;
  };

  const landingBookCover = makeAbsoluteUrl(landingBook.cover);

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: pageTitle,
    url: pageUrl,
    description: pageDescription,
    author: {
      '@type': 'Person',
      name: 'Jerry Evanoff',
      url: 'https://jerryevanoff.com'
    },
    mainEntity: {
      '@type': 'BookSeries',
      name: 'Sam Norris Murder Mystery Series',
      author: {
        '@type': 'Person',
        name: 'Jerry Evanoff',
        url: 'https://jerryevanoff.com'
      },
      genre: [
        'Amateur Sleuth Mystery',
        'Murder Mystery',
        'Modern Whodunit',
        'Soft-Boiled Mystery'
      ],
      description:
        'The Sam Norris Murder Mystery Series follows tech-savvy programmer and amateur sleuth Sam Norris through modern whodunit mysteries built around old-school puzzle plots.'
    }
  };

  const readingOrderSchema = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Sam Norris Murder Mystery Series Reading Order',
    itemListElement: allSamBooks
      .filter(book => book.readingOrder || book.readingOrderBookNumber)
      .sort((a, b) => {
        const numA = a.readingOrderBookNumber ?? parseFloat(a.readingOrder);
        const numB = b.readingOrderBookNumber ?? parseFloat(b.readingOrder);
        return numA - numB;
      })
      .map((book, index) => ({
        '@type': 'ListItem',
        position: index + 1,
        name: book.title,
        url: book.webLink
          ? makeAbsoluteUrl(book.webLink)
          : book.buyLink || pageUrl
      }))
  };

  return (
    <div className="lp-container">
      <Helmet>
        <title>{pageTitle}</title>

        <meta name="description" content={pageDescription} />

        <meta
          name="keywords"
          content="Jerry Evanoff, Sam Norris Murder Mystery Series, amateur sleuth murder mystery, modern whodunit, tech-savvy sleuth, programmer detective, soft-boiled mystery, Agatha Christie-style mystery, murder mystery series reading order"
        />

        <meta name="author" content="Jerry Evanoff" />

        <link rel="canonical" href={pageUrl} />

        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:image" content={landingBookCover} />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:site_name" content="Jerry Evanoff" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content={landingBookCover} />

        <script type="application/ld+json">
          {JSON.stringify(webPageSchema)}
        </script>

        <script type="application/ld+json">
          {JSON.stringify(readingOrderSchema)}
        </script>
      </Helmet>

      <section className="lp-main-layout">
        <div className="lp-hero-col">
          <img
            src={landingBook.cover}
            alt={landingBook.title}
            className="lp-hero-cover"
          />

          <p className="lp-hero-description">{landingBook.landingPageDescription}</p>

          {landingBook.freePromoText && (
            <p className="lp-hero-promo">{landingBook.freePromoText}</p>
          )}

          <a
            href={landingBook.buyLink}
            target="_blank"
            rel="noopener noreferrer"
            className="lp-hero-button"
          >
            {landingBook.freePromoText
              ? 'Get Free at Amazon - eBook Only'
              : 'Read Sample or Purchase at Amazon (Including in KU)'}
          </a>
        </div>

        <div className="lp-card-stack">
          {bookOne && bookOne.id !== landingBook.id && (
            <div className="lp-card lp-start-card">
              <h3 className="lp-card-title">Start the Series at Book One</h3>
              &nbsp;

              <img
                src={bookOne.cover}
                alt={bookOne.title}
                className="lp-start-cover"
              />

              <p className="lp-card-text">
                {bookOne.landingPageDescription}
              </p>

              {bookOne.webLink ? (
                <Link
                  to={bookOne.webLink}
                  className="lp-text-link"
                >
                  Start with {bookOne.title}
                </Link>
              ) : (
                bookOne.buyLink && (
                  <a
                    href={bookOne.buyLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="lp-text-link"
                  >
                    Start with {bookOne.title}
                  </a>
                )
              )}
            </div>
          )}

          <div className="lp-card">
            <h3 className="lp-card-title">What’s Next</h3>
            <p className="lp-card-text">
              Curious what's coming after Book 1? Head to the series preview.
            </p>

            <Link to="/next" className="lp-card-button">
              Explore What's Next
            </Link>
          </div>

          <div className="lp-card">
            <h3 className="lp-card-title">Signed Paperbacks & Gifts</h3>
            <p className="lp-card-text">
              Prefer paperbacks or want to support the series? Signed copies and bundles.
            </p>

            <a
              href="https://www.etsy.com/shop/JerryEvanoffAuthor"
              target="_blank"
              rel="noopener noreferrer"
              className="lp-card-button"
            >
              Shop paperbacks & bundles
            </a>
          </div>
        </div>
      </section>

      <section className="reading-order-container">
        <h2 className="reading-order-title">Reading Order</h2>

        <div className="reading-order-bar">
          {allSamBooks
            .filter(book => book.readingOrder || book.readingOrderBookNumber)
            .sort((a, b) => {
              const numA = a.readingOrderBookNumber ?? parseFloat(a.readingOrder);
              const numB = b.readingOrderBookNumber ?? parseFloat(b.readingOrder);
              return numA - numB;
            })
            .map((book) => {
              const published = book.bookStatus?.toLowerCase() === 'published';
              const overFifty = !published && book.progress > 50;
              const orderLabel = book.readingOrder ?? '?';

              let circleClass = 'coming-soon';
              if (published) {
                circleClass = 'published';
              } else if (overFifty) {
                circleClass = 'in-progress-yellow';
              }

              const circleElement = book.webLink ? (
                <Link
                  to={book.webLink}
                  className={`reading-order-circle ${circleClass}`}
                >
                  {orderLabel}
                </Link>
              ) : book.buyLink ? (
                <a
                  href={book.buyLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={`reading-order-circle ${circleClass}`}
                >
                  {orderLabel}
                </a>
              ) : (
                <div className={`reading-order-circle ${circleClass}`}>
                  {orderLabel}
                </div>
              );

              return (
                <div className="reading-order-step" key={book.id}>
                  {circleElement}

                  <div className="reading-order-tooltip">
                    <strong>{book.title}</strong>
                    <br />
                    {published ? (
                      <>
                        Status: {book.bookStatus || 'Published'} <br />
                        Published: {book.publishedDate || ''}
                      </>
                    ) : (
                      <>
                        Status: {book.bookStatus || 'In Progress'} <br />
                        Progress: {book.progress || 0}%
                      </>
                    )}
                  </div>
                </div>
              );
            })}
        </div>
      </section>
    </div>
  );
};

export default LandingPage;